import React from "react";
import { cn } from "./cn";
import { Input } from "./Field";
import { bunToken } from './chartTheme';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const PICKER_FALLBACK = "#000000";

/**
 * Controlled color setting field with both a native picker and editable text.
 * Invalid or incomplete text remains visible while the picker receives a valid
 * six-digit hex value.
 */
export default function ColorInput({
  id,
  name,
  value,
  onChange,
  disabled,
  "aria-label": ariaLabel,
  className,
  pickerLabel = "Choose color",
}) {
  const textValue = value ?? "";
  const pickerValue = HEX_COLOR.test(textValue) ? textValue : PICKER_FALLBACK;

  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      <input
        id={id ? `${id}-picker` : undefined}
        type="color"
        value={pickerValue}
        onChange={onChange}
        disabled={disabled}
        aria-label={pickerLabel}
        className={cn(
          "h-11 w-11 shrink-0 cursor-pointer rounded-bun border border-bun-line bg-bun-surface p-1",
          "outline-none focus:border-bun-blue focus:ring-[3px] focus:ring-bun-ring",
          "disabled:cursor-not-allowed disabled:bg-bun-mist disabled:opacity-60",
          "sm:h-[38px] sm:w-[38px]"
        )}
      />
      <Input
        id={id}
        name={name}
        type="text"
        value={textValue}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel}
        spellCheck={false}
        className="min-w-0"
      />
    </div>
  );
}

/** Shared conditional-formatting swatches; saved values remain concrete colors. */
export function getFormattingPalette() {
  return [[['Red', 'danger'], ['Amber', 'warning'], ['Green', 'success'], ['Blue', 'info'], ['Purple', 'special'], ['Neutral', null]].map(([name, tone]) => ({
    name,
    bg: bunToken(tone ? `--bun-${tone}-bg` : '--bun-mist-deep'),
    text: bunToken(tone ? `--bun-${tone}` : '--bun-text'),
  }))];
}

ColorInput.Swatch = function Swatch({ color, label, selected, onClick, disabled }) {
  return <button type="button" aria-label={label} aria-pressed={selected} disabled={disabled} onClick={onClick} title={label}
    className={cn('inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-bun border border-bun-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bun-ring disabled:opacity-60 sm:h-8 sm:w-8', selected && 'ring-2 ring-bun-blue')}>
    <span className="h-5 w-5 rounded-bun-sm" style={{ backgroundColor: color }} />
  </button>;
};
